import { HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import moment from 'moment';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
    BehaviorSubject,
    catchError,
    map,
    Observable,
    of,
    take,
    timeout,
} from 'rxjs';
import { FuseConfirmationService } from '../../../../@fuse/services/confirmation';
import { HttpService } from '../../../core/shared/http.service';
import { ClientStatus } from '../client-management/client-management.types';
import { EmployeeCertificate } from '../employee-management/employee-management.types';
import {
    PartTimerDischargeList,
    PartTimerDischargeListPaginationInterface,
    PartTimerList,
    PartTimerListPaginationInterface,
    PartTimerResponse,
    PartTimerWorkingHistory,
    PartTimerWorkingHoursPaginationInterface,
} from './part-timer-management.types';

@Injectable({
    providedIn: 'root',
})
export class PartTimerManagementService {
    private _pagination: BehaviorSubject<PartTimerListPaginationInterface | null> =
        new BehaviorSubject(null);
    private _partTimerList: BehaviorSubject<PartTimerList[]> =
        new BehaviorSubject([]);
    private _partTimerListForExport: BehaviorSubject<PartTimerList[]> =
        new BehaviorSubject([]);
    private _partTimerDetails: BehaviorSubject<PartTimerResponse | null> =
        new BehaviorSubject(null);
    private _paginationHistory: BehaviorSubject<PartTimerWorkingHoursPaginationInterface | null> =
        new BehaviorSubject(null);
    private _partTimerWorkingHistoryList: BehaviorSubject<
        PartTimerWorkingHistory[]
    > = new BehaviorSubject([]);

    private _paginationDischarge: BehaviorSubject<PartTimerDischargeListPaginationInterface | null> =
        new BehaviorSubject(null);
    private _partTimerDischargeList: BehaviorSubject<PartTimerDischargeList[]> =
        new BehaviorSubject([]);
    private _partTimerManagerDischargeList: BehaviorSubject<
        PartTimerDischargeList[]
    > = new BehaviorSubject([]);

    //for current discharge list
    private _paginationCurrentDischarge: BehaviorSubject<PartTimerListPaginationInterface | null> =
        new BehaviorSubject(null);
    private _partTimerCurrentDischargeList: BehaviorSubject<PartTimerList[]> =
        new BehaviorSubject([]);

    constructor(
        private _http: HttpService,
        private _fuseConfirmationService: FuseConfirmationService,
        private _messageService: NzMessageService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    get pagination$() {
        return this._pagination;
    }

    get paginationHistory$() {
        return this._paginationHistory;
    }

    get partTimerList$() {
        return this._partTimerList;
    }

    get partTimerListForExport$() {
        return this._partTimerListForExport;
    }

    get partTimerDetails$() {
        return this._partTimerDetails;
    }

    get partTimerWorkingHistoryList$() {
        return this._partTimerWorkingHistoryList;
    }

    get paginationDischarge$() {
        return this._paginationDischarge;
    }

    get partTimerDischargeList$() {
        return this._partTimerDischargeList;
    }

    get partTimerManagerDischargeList$() {
        return this._partTimerManagerDischargeList;
    }

    get paginationCurrentDischarge$() {
        return this._paginationCurrentDischarge;
    }

    get partTimerCurrentDischargeList$() {
        return this._partTimerCurrentDischargeList;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Format a Date (or date-like) to local YYYY-MM-DD.
     * Avoids UTC timezone shift from toISOString().
     */
    private _formatLocalDate(date: Date): string {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }

    /**
     * Prepare certificates FormData for multipart request
     * Matches employee-management.service.ts implementation
     */
    private prepareEmployeeCertificatesFormData(
        formData: FormData,
        certificates: EmployeeCertificate[]
    ): void {
        if (!certificates || certificates.length === 0) {
            return;
        }

        certificates.forEach((cert, index) => {
            // For new certificates: send file and metadata
            if (cert.file instanceof File) {
                formData.append(`certificates[${index}].file`, cert.file);
            }

            // Append certificate metadata
            formData.append(
                `certificates[${index}].certificate_type_id`,
                cert.certificate_type_id
            );
            formData.append(
                `certificates[${index}].has_expiry`,
                cert.has_expiry ? '1' : '0'
            );

            if (cert.has_expiry && cert.expiry_date) {
                // Convert Date object to YYYY-MM-DD string format
                let expiryDate: string;
                if (typeof cert.expiry_date === 'string') {
                    expiryDate = cert.expiry_date;
                } else {
                    // Date object - convert to local YYYY-MM-DD (avoid UTC shift)
                    expiryDate = this._formatLocalDate(cert.expiry_date as any);
                }
                formData.append(
                    `certificates[${index}].expiry_date`,
                    expiryDate
                );
            }
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    public getPartTimerById(id: string) {
        return this._http
            .get<any>(`/v1/employee/get-part-timer`, {
                id,
            })
            .pipe(
                take(1),
                map((response) => {
                    if (+response.code === HttpStatusCode.Ok) {
                        this._partTimerDetails.next(response.data);

                        return response.data;
                    } else {
                        this._messageService.error(response.message, {
                            nzDuration: 5000,
                        });
                    }

                    return null;
                }),
                catchError((error) => {
                    this._fuseConfirmationService.open({
                        title: 'Oops!',
                        message: error.message,
                        actions: {
                            confirm: {
                                show: false,
                            },
                            cancel: {
                                label: 'OK',
                            },
                        },
                    });

                    throw new Error(
                        'An error occurred while fetching the part-timer details, please try again later.'
                    );
                })
            );
    }

    /**
     * Same request as getPartTimerById without pushing into partTimerDetails$, so the Regenerate Contract dialog
     * opened from the contractor list never replaces the record shown in an open details drawer.
     */
    public fetchPartTimerById(id: string): Observable<any> {
        return this._http
            .get<any>(`/v1/employee/get-part-timer`, {
                id,
            })
            .pipe(
                take(1),
                map((response) => {
                    if (+response.code === HttpStatusCode.Ok) {
                        return response.data;
                    }
                    this._messageService.error(response.message, {
                        nzDuration: 5000,
                    });
                    return null;
                }),
                catchError((error) => {
                    this._messageService.error(
                        error?.message ||
                            'An error occurred while fetching the contractor details, please try again later.',
                        { nzDuration: 5000 }
                    );
                    return of(null);
                })
            );
    }

    /**
     * Stores a regenerated contract (PDF from the agreement template) as the active contract, expiring at the end
     * of `endDate` (yyyy-MM-dd), and emails it to the employee and HR. Resolves to the backend's result
     * ({ contract_url, expired_at, employee_email_sent, hr_email_sent }) or null when refused or failed.
     */
    public regenerateContract(
        id: string,
        contract: File,
        endDate: string
    ): Observable<{
        contract_url: string;
        expired_at: string;
        employee_email_sent: boolean;
        hr_email_sent: boolean;
    } | null> {
        const formData = new FormData();
        formData.append('contract', contract);
        formData.append('end_date', endDate);

        return this._http
            .post<any>(`/v2/employee/${id}/contract/regenerate`, formData)
            .pipe(
                take(1),
                map((response) => {
                    if (+response.code === HttpStatusCode.Ok) {
                        return response.data;
                    }
                    this._messageService.error(response.message, {
                        nzDuration: 5000,
                    });
                    return null;
                }),
                catchError((error) => {
                    this._messageService.error(
                        error?.message ||
                            'An error occurred while regenerating the contract, please try again later.',
                        { nzDuration: 5000 }
                    );
                    return of(null);
                })
            );
    }

    /**
     * Helper method to prepare part-timer certificates for FormData
     * @param formData The FormData object to append certificates to
     * @param certificates Array of employee certificates
     */
    private preparePartTimerCertificatesFormData(
        formData: FormData,
        certificates: any[]
    ): void {
        if (!certificates || certificates.length === 0) {
            return;
        }

        certificates.forEach((cert, index) => {
            // Check if this is an existing certificate (has id and certificate_url)
            const isExistingCertificate = cert.id && cert.certificate_url;

            if (isExistingCertificate) {
                // For existing certificates: send only ID and metadata (no file)
                formData.append(`certificates[${index}].id`, cert.id);
                formData.append(
                    `certificates[${index}].certificate_type_id`,
                    cert.certificate_type_id
                );
                formData.append(
                    `certificates[${index}].has_expiry`,
                    cert.has_expiry ? '1' : '0'
                );

                if (cert.has_expiry && cert.expiry_date) {
                    let expiryDate: string;
                    if (typeof cert.expiry_date === 'string') {
                        expiryDate = cert.expiry_date;
                    } else {
                        expiryDate = (cert.expiry_date as any)
                            .toISOString()
                            .split('T')[0];
                    }
                    formData.append(
                        `certificates[${index}].expiry_date`,
                        expiryDate
                    );
                }
            } else {
                // For new certificates: send file and metadata
                // Append certificate file
                if (cert.file instanceof File) {
                    formData.append(`certificates[${index}].file`, cert.file);
                }

                // Append certificate metadata
                formData.append(
                    `certificates[${index}].certificate_type_id`,
                    cert.certificate_type_id
                );
                formData.append(
                    `certificates[${index}].has_expiry`,
                    cert.has_expiry ? '1' : '0'
                );

                if (cert.has_expiry && cert.expiry_date) {
                    // Convert Date object to YYYY-MM-DD string format
                    let expiryDate: string;
                    if (typeof cert.expiry_date === 'string') {
                        expiryDate = cert.expiry_date;
                    } else {
                        // Date object - convert to ISO format
                        expiryDate = (cert.expiry_date as any)
                            .toISOString()
                            .split('T')[0];
                    }
                    formData.append(
                        `certificates[${index}].expiry_date`,
                        expiryDate
                    );
                }
            }
        });
    }

    public updateClient(
        id: string,
        fullname: string,
        email: string,
        contact_number: string,
        address: string,
        gender: string,
        formatted_address: string = null,
        location_lat: string = null,
        location_lng: string = null,
        street_number: string = null,
        street: string = null,
        city: string = null,
        state: string = null,
        postal_code: string = null,
        country: string = null,
        country_code: string = null,
        place_id: string = null,
        certificates: any[] = [],
        bank_name: string = null,
        bank_acc: string = null,
        kin_name: string = null,
        kin_contact: string = null,
        nric: string = null
    ) {
        const now = (moment().unix() * 1000).toString();

        // Prepare FormData for multipart request
        const formData = new FormData();
        formData.append('fullname', fullname);
        formData.append('email', email || '');
        formData.append('contact_number', contact_number || '');
        formData.append('address', address || '');
        formData.append('gender', gender);

        // Address fields
        if (formatted_address)
            formData.append('formatted_address', formatted_address);
        if (location_lat) formData.append('location_lat', location_lat);
        if (location_lng) formData.append('location_lng', location_lng);
        if (street_number) formData.append('street_number', street_number);
        if (street) formData.append('street', street);
        if (city) formData.append('city', city);
        if (state) formData.append('state', state);
        if (postal_code) formData.append('postal_code', postal_code);
        if (country) formData.append('country', country);
        if (country_code) formData.append('country_code', country_code);
        if (place_id) formData.append('place_id', place_id);

        // Part-timer specific fields (bank and kin info)
        if (bank_name) formData.append('bank_name', bank_name);
        if (bank_acc) formData.append('bank_acc', bank_acc);
        if (kin_name) formData.append('kin_name', kin_name);
        if (kin_contact) formData.append('kin_contact', kin_contact);
        if (nric) formData.append('nric', nric);

        // Add certificates using helper method
        this.preparePartTimerCertificatesFormData(formData, certificates);

        return this._http
            .put<any>(`/v1/employee/update?id=${id}`, formData)
            .pipe(
                take(1),
                map((response) => {
                    if (+response.code === HttpStatusCode.Ok) {
                        const updatedClientList = this._partTimerList
                            .getValue()
                            .map((client) => {
                                if (client.id === id) {
                                    client.fullname = fullname;
                                    client.email = email;
                                    client.contact_number = contact_number;
                                    client.address = address;
                                    client.gender = gender;
                                    client.updated_at = now;
                                }

                                return client;
                            });

                        this._partTimerList.next(updatedClientList);

                        return true;
                    } else {
                        this._messageService.error(response.message, {
                            nzDuration: 5000,
                        });

                        return false;
                    }
                }),
                catchError((error) => {
                    this._fuseConfirmationService.open({
                        title: 'Oops!',
                        message: error.message,
                        actions: {
                            confirm: {
                                show: false,
                            },
                            cancel: {
                                label: 'OK',
                            },
                        },
                    });

                    throw new Error(
                        'An error occurred while updating the part-timer, please try again later.'
                    );
                })
            );
    }

    public updateEmployeeType(id: string, type: string): Observable<boolean> {
        const formData = new FormData();
        formData.append('type_of_employment', type);

        return this._http
            .put<any>(`/v1/employee/update/type?id=${id}`, formData)
            .pipe(
                take(1),
                map((response) => {
                    if (+response.code === HttpStatusCode.Ok) {
                        // Update the details if currently loaded
                        const currentDetails =
                            this._partTimerDetails.getValue();
                        if (
                            currentDetails &&
                            currentDetails.employee_details.id === id
                        ) {
                            currentDetails.pt_details.type_of_employment = type;
                            this._partTimerDetails.next(currentDetails);
                        }

                        this._messageService.success(
                            'Employee type updated successfully'
                        );
                        return true;
                    } else {
                        this._messageService.error(response.message, {
                            nzDuration: 5000,
                        });
                        return false;
                    }
                }),
                catchError((error) => {
                    this._fuseConfirmationService.open({
                        title: 'Oops!',
                        message: error.message,
                        actions: {
                            confirm: {
                                show: false,
                            },
                            cancel: {
                                label: 'OK',
                            },
                        },
                    });

                    throw new Error(
                        'An error occurred while updating employee type, please try again later.'
                    );
                })
            );
    }

    public getPartTimerDischargeListWithPagination(
        page_number: number = 0,
        page_size: number = 25,
        search: string = null,
        sort: string = 'created_at',
        order: 'asc' | 'desc' | '' = 'desc',
        start_at?: string
    ) {
        return this._http
            .post<PartTimerDischargeListPaginationInterface>(
                `/v1/employee/list-pending-discharge-with-paginate`,
                null,
                {
                    keyword: search || '',
                    page_size: page_size,
                    page_num: page_number + 1,
                    sort_by: sort,
                    sort_direction: order,
                    start_date: start_at || '',
                }
            )
            .pipe(
                map((response) => {
                    if (+response.code === HttpStatusCode.Ok) {
                        const records = response.data.records;
                        console.log('Records: ', records);

                        this._paginationDischarge.next(response.data);
                        this._partTimerDischargeList.next(records);
                    } else {
                        this._messageService.error(response.message, {
                            nzDuration: 5000,
                        });
                    }
                }),
                catchError((error) => {
                    this._fuseConfirmationService.open({
                        title: 'Oops!',
                        message: error.message,
                        actions: {
                            confirm: {
                                show: false,
                            },
                            cancel: {
                                label: 'OK',
                            },
                        },
                    });

                    throw new Error(
                        'An error occurred while fetching the part-timer list, please try again later.'
                    );
                })
            );
    }

    public getPartTimerManagerDischargeListWithPagination(
        page_number: number = 0,
        page_size: number = 25,
        search: string = null,
        sort: string = 'created_at',
        order: 'asc' | 'desc' | '' = 'desc',
        status?: string,
        decision?: string
    ) {
        return this._http
            .post<PartTimerDischargeListPaginationInterface>(
                `/v1/employee/list-history-discharge-with-paginate`,
                null,
                {
                    keyword: search || '',
                    page_size: page_size,
                    page_num: page_number + 1,
                    sort_by: sort,
                    sort_direction: order,
                    status: status || '',
                    decision: decision || '',
                }
            )
            .pipe(
                map((response) => {
                    if (+response.code === HttpStatusCode.Ok) {
                        const records = response.data.records;
                        console.log('Records: ', records);

                        this._paginationDischarge.next(response.data);
                        this._partTimerManagerDischargeList.next(records);
                    } else {
                        this._messageService.error(response.message, {
                            nzDuration: 5000,
                        });
                    }
                }),
                catchError((error) => {
                    this._fuseConfirmationService.open({
                        title: 'Oops!',
                        message: error.message,
                        actions: {
                            confirm: {
                                show: false,
                            },
                            cancel: {
                                label: 'OK',
                            },
                        },
                    });

                    throw new Error(
                        'An error occurred while fetching the part-timer list, please try again later.'
                    );
                })
            );
    }

    public getPartTimerCurrentDischargeWithPagination(
        page_number: number = 0,
        page_size: number = 25,
        search: string = null,
        sort: string = 'id',
        order: 'asc' | 'desc' | '' = 'desc',
        start_at?: string
    ) {
        return this._http
            .post<PartTimerListPaginationInterface>(
                `/v1/employee/list-discharge-pt`,
                null,
                {
                    keyword: search || '',
                    page_size: page_size,
                    page_num: page_number + 1,
                    sort_by: sort,
                    sort_direction: order,
                }
            )
            .pipe(
                map((response) => {
                    if (+response.code === HttpStatusCode.Ok) {
                        const records = response.data.records;
                        console.log('Records: ', records);

                        this._paginationCurrentDischarge.next(response.data);
                        this._partTimerCurrentDischargeList.next(records);
                    } else {
                        this._messageService.error(response.message, {
                            nzDuration: 5000,
                        });
                    }
                }),
                catchError((error) => {
                    this._fuseConfirmationService.open({
                        title: 'Oops!',
                        message: error.message,
                        actions: {
                            confirm: {
                                show: false,
                            },
                            cancel: {
                                label: 'OK',
                            },
                        },
                    });

                    throw new Error(
                        'An error occurred while fetching the discharge part-timer list, please try again later.'
                    );
                })
            );
    }

    public getPartTimerListWithPagination(
        pageNumber: number = 0,
        pageSize: number = 10,
        searchQuery: string = '',
        sortBy: string = 'id',
        sortDirection: string = 'asc',
        startDate?: string,
        addressValidation?: boolean,
        // My Employees hidden 2026-10-20: client finds it confusing; restore when asked
        // managedByMe: boolean = true,
        managedByMe: boolean = false, // Filter to show only employees managed by current user
        roleFilter: string = 'both', // Filter by role: 'both', 'parttime', 'contractor', or 'fulltime'
        isChecked?: boolean, // Filter by the checking workflow flag, omitted for "All"
        workPass?: boolean // Filter by Work Pass (id_type FIN), omitted for "All"
    ): Observable<PartTimerListPaginationInterface> {
        const params: any = {
            keyword: searchQuery || '',
            page_size: pageSize,
            page_num: pageNumber + 1,
            sort_by: sortBy,
            sort_direction: sortDirection,
            start_date: startDate || '',
            address_validation: addressValidation,
            managed_by_me: managedByMe,
            role_filter: roleFilter,
        };

        if (isChecked === true || isChecked === false) {
            params.is_checked = isChecked;
        }

        if (workPass === true || workPass === false) {
            params.work_pass = workPass;
        }

        return this._http
            .post<PartTimerListPaginationInterface>(
                `/v1/employee/list-pt-with-paginate`,
                null,
                params
            )
            .pipe(
                map((response) => {
                    if (+response.code === HttpStatusCode.Ok) {
                        const records = response.data.records;
                        console.log('Records: ', records);

                        this._pagination.next(response.data);
                        this._partTimerList.next(records);
                    } else {
                        this._messageService.error(response.message, {
                            nzDuration: 5000,
                        });
                    }
                    return response.data;
                }),
                catchError((error) => {
                    this._fuseConfirmationService.open({
                        title: 'Oops!',
                        message: error.message,
                        actions: {
                            confirm: {
                                show: false,
                            },
                            cancel: {
                                label: 'OK',
                            },
                        },
                    });

                    throw new Error(
                        'An error occurred while fetching the part-timer list, please try again later.'
                    );
                })
            );
    }

    public getPartTimerListExport(
        page_number: number = 0,
        page_size: number = 1000000,
        search: string = null,
        sort: string = 'id',
        order: 'asc' | 'desc' | '' = 'desc',
        start_at?: string,
        // My Employees hidden 2026-10-20: client finds it confusing; restore when asked
        // managedByMe: boolean = true,
        managedByMe: boolean = false, // Filter to show only employees managed by current user
        addressValidation?: boolean,
        roleFilter: string = 'both', // Filter by role: 'both', 'parttime', 'contractor', or 'fulltime'
        isChecked?: boolean, // Filter by the checking workflow flag, omitted for "All"
        workPass?: boolean // Filter by Work Pass (id_type FIN), omitted for "All"
    ) {
        const params: any = {
            keyword: search || '',
            page_size: page_size,
            page_num: page_number + 1,
            sort_by: sort,
            sort_direction: order,
            start_date: start_at || '',
            managed_by_me: managedByMe,
            address_validation: addressValidation,
            role_filter: roleFilter,
        };

        if (isChecked === true || isChecked === false) {
            params.is_checked = isChecked;
        }

        if (workPass === true || workPass === false) {
            params.work_pass = workPass;
        }

        return this._http
            .post<PartTimerListPaginationInterface>(
                `/v1/employee/list-pt-for-export`,
                null,
                params
            )
            .pipe(
                map((response) => {
                    if (+response.code === HttpStatusCode.Ok) {
                        const records = response.data.records;
                        console.log('Records: ', records);
                        this._partTimerListForExport.next(records);
                    } else {
                        this._messageService.error(response.message, {
                            nzDuration: 5000,
                        });
                    }
                }),
                catchError((error) => {
                    this._fuseConfirmationService.open({
                        title: 'Oops!',
                        message: error.message,
                        actions: {
                            confirm: {
                                show: false,
                            },
                            cancel: {
                                label: 'OK',
                            },
                        },
                    });

                    throw new Error(
                        'An error occurred while fetching the part-timer list for export, please try again later.'
                    );
                })
            );
    }

    public updateStatus(id: string, status: boolean) {
        return this._http
            .put(`/v1/employee/update-status`, null, {
                id,
                status: status ? 1 : 0,
            })
            .pipe(
                take(1),
                map((response) => {
                    if (+response.code === HttpStatusCode.Ok) {
                        const updatedClientList = this._partTimerList
                            .getValue()
                            .map((client) => {
                                if (client.id === id) {
                                    client.status = status
                                        ? ClientStatus.active
                                        : ClientStatus.inactive;
                                    client.updated_at = (
                                        moment().unix() * 1000
                                    ).toString();
                                }

                                return client;
                            });

                        this._partTimerList.next(updatedClientList);

                        return true;
                    } else {
                        this._messageService.error(response.message, {
                            nzDuration: 5000,
                        });

                        return false;
                    }
                }),
                catchError((error) => {
                    this._fuseConfirmationService.open({
                        title: 'Oops!',
                        message: error.message,
                        actions: {
                            confirm: {
                                show: false,
                            },
                            cancel: {
                                label: 'OK',
                            },
                        },
                    });

                    throw new Error(
                        'An error occurred while updating the part-timer status, please try again later.'
                    );
                })
            );
    }

    /**
     * Toggle the "Checked" flag used by the client checking workflow.
     * Updates the list row and, when loaded, the currently opened details.
     */
    public updateChecked(id: string, checked: boolean) {
        return this._http
            .put(`/v2/employee/${id}/checked`, { checked })
            .pipe(
                take(1),
                map((response) => {
                    if (+response.code === HttpStatusCode.Ok) {
                        const checkedAt = checked
                            ? (moment().unix() * 1000).toString()
                            : null;

                        const updatedClientList = this._partTimerList
                            .getValue()
                            .map((client) => {
                                if (client.id === id) {
                                    client.is_checked = checked;
                                    client.checked_at = checkedAt;
                                }

                                return client;
                            });

                        this._partTimerList.next(updatedClientList);

                        // Update the details if currently loaded
                        const currentDetails =
                            this._partTimerDetails.getValue();
                        if (
                            currentDetails &&
                            currentDetails.employee_details.id === id
                        ) {
                            currentDetails.employee_details.is_checked =
                                checked;
                            currentDetails.employee_details.checked_at =
                                checkedAt;
                            this._partTimerDetails.next(currentDetails);
                        }

                        this._messageService.success(
                            checked
                                ? 'Marked as checked'
                                : 'Checked mark removed'
                        );

                        return true;
                    } else {
                        this._messageService.error(response.message, {
                            nzDuration: 5000,
                        });

                        return false;
                    }
                }),
                catchError((error) => {
                    this._fuseConfirmationService.open({
                        title: 'Oops!',
                        message: error.message,
                        actions: {
                            confirm: {
                                show: false,
                            },
                            cancel: {
                                label: 'OK',
                            },
                        },
                    });

                    throw new Error(
                        'An error occurred while updating the checked mark, please try again later.'
                    );
                })
            );
    }

    /**
     * Look up an existing employee by NRIC/IC to gate the registration flow.
     * Staff-authenticated (normal admin JWT). Returns the lookup payload:
     *   { match_status, status?, employee_id?, prefill? }
     * match_status is one of:
     *   NOT_FOUND | ACTIVE | BLACKLISTED | OTHER_STATUS | POSSIBLE_LEGACY_MATCH
     * Returns null on a transport/error condition (caller treats as "could not verify").
     */
    public lookupByNric(nric: string): Observable<any> {
        return this._http
            .get<any>(`/v1/employee/register/lookup-by-nric`, {
                nric,
            })
            .pipe(
                // A hung connection must resolve to the blocking 'error' state
                // instead of leaving the form stuck on 'checking' forever.
                timeout(15000),
                take(1),
                map((response) => {
                    if (+response.code === HttpStatusCode.Ok) {
                        return response.data;
                    } else {
                        this._messageService.error(response.message, {
                            nzDuration: 5000,
                        });
                        return null;
                    }
                }),
                catchError((error) => {
                    // A lookup failure should not open a blocking dialog; surface softly.
                    console.error('IC lookup failed:', error);
                    return of(null);
                })
            );
    }

    public createPT(
        client_name: string,
        contact_email: string,
        contact_number: string,
        nationality: string,
        id_type: string,
        nric: string,
        passport_expiry: string,
        date_of_birth: string,
        gender: string,
        residential_address: string,
        postal_code: string,
        formatted_address: string = null,
        location_lat: string = null,
        location_lng: string = null,
        street_number: string = null,
        street: string = null,
        city: string = null,
        state: string = null,
        country_from_google: string = null,
        country_code: string = null,
        place_id: string = null,
        kin_name: string,
        kin_contact: string,
        bank_name: string,
        bank_acc: string,
        highest_education: string,
        school: string,
        country: string,
        eduStartTimestamp: any,
        eduEndTimestamp: any,
        high_standard: string,
        type_of_employment: string,
        position_applied: string,
        pr_start_date: string,
        place_of_birth: string,
        age: string,
        race: string,
        religion: string,
        marital_status: string,
        earliest_availability: string,
        signature: string,
        certificates: EmployeeCertificate[],
        contract: File,
        qna: {
            where_know_us: string;
            fully_vaccinated: number;
            relatives_in_company: number;
            relatives_info: string;
            any_illness: number;
            physical: number;
            mental_illness: number;
            medication: number;
            medication_details: string;
            found_guilty: number;
            any_investigation: number;
            objection_undergo: number;
            alcohol_drug_addiction: number;
            undischarged_bankrupt: number;
            dismissed_or_suspended: number;
            business_undertaking: number;
        },
        working_exps: {
            previous_company_name: string;
            position: string;
            date_start: string;
            date_left: string;
            reason_for_leaving: string;
        }[],
        requiresDocument: boolean,
        // When set, submit as a resubmission that updates the existing ACTIVE
        // employee in place instead of creating a new record.
        employeeId?: string
    ) {
        const formData = new FormData();

        // Append all text fields
        formData.append('client_name', client_name);
        formData.append('contact_email', contact_email);
        formData.append('contact_number', contact_number);
        formData.append('nationality', nationality);
        formData.append('id_type', id_type);
        formData.append('nric', nric);
        formData.append('passport_expiry', passport_expiry);
        formData.append('date_of_birth', date_of_birth);
        formData.append('gender', gender);
        formData.append('residential_address', residential_address);
        formData.append('postal_code', postal_code);

        // Append address data with same naming convention as project-location-management
        if (formatted_address)
            formData.append('formatted_address', formatted_address);
        if (location_lat) formData.append('location_lat', location_lat);
        if (location_lng) formData.append('location_lng', location_lng);
        if (street_number) formData.append('street_number', street_number);
        if (street) formData.append('street', street);
        if (city) formData.append('city', city);
        if (state) formData.append('state', state);
        if (country) formData.append('country', country);
        if (country_from_google)
            formData.append('country_from_google', country_from_google);
        if (country_code) formData.append('country_code', country_code);
        if (place_id) formData.append('place_id', place_id);
        if (postal_code)
            formData.append('postal_code_from_google', postal_code);

        formData.append('kin_name', kin_name);
        formData.append('kin_contact', kin_contact);
        formData.append('bank_name', bank_name);
        formData.append('bank_acc', bank_acc);
        formData.append('highest_education', highest_education);
        formData.append('school', school);
        formData.append('edu_start', eduStartTimestamp);
        formData.append('edu_grad', eduEndTimestamp);
        formData.append('high_standard', high_standard);
        formData.append('type_of_employment', type_of_employment);
        formData.append('position_applied', position_applied);
        formData.append('earliest_availability', earliest_availability);
        formData.append('pr_start_date', pr_start_date);
        formData.append('place_of_birth', place_of_birth);
        formData.append('age', age);
        formData.append('race', race);
        formData.append('religion', religion);
        formData.append('marital_status', marital_status);
        formData.append('signature', signature);
        if (requiresDocument === true) {
            formData.append('requires_document', 'true');
        } else {
            formData.append('requires_document', 'false');
        }

        // Append certificates array
        this.prepareEmployeeCertificatesFormData(formData, certificates);

        if (contract) {
            formData.append('contract', contract);
        }

        // Append complex objects as JSON strings
        formData.append('qna', JSON.stringify(qna));
        formData.append('working_exps', JSON.stringify(working_exps));

        // Resubmission mode: update an existing ACTIVE employee in place.
        const endpoint = employeeId
            ? `/v1/employee/register/resubmit/part-timer`
            : `/v1/employee/create/part-timer`;
        if (employeeId) {
            formData.append('employee_id', employeeId);
        }

        return this._http
            .post<any>(endpoint, formData)
            .pipe(
                take(1),
                map((response) => {
                    if (+response.code === HttpStatusCode.Ok) {
                        return true;
                    } else {
                        this._messageService.error(response.message, {
                            nzDuration: 5000,
                        });

                        return false;
                    }
                }),
                catchError((error) => {
                    this._fuseConfirmationService.open({
                        title: 'Oops!',
                        message: error.message,
                        actions: {
                            confirm: {
                                show: false,
                            },
                            cancel: {
                                label: 'OK',
                            },
                        },
                    });

                    throw new Error(
                        'An error occurred while registering, please try again later.'
                    );
                })
            );
    }

    public handleManageDischarge(
        id: string,
        comment: string,
        date_of: string,
        contract: File
    ) {
        const formData = new FormData();

        // Append all text fields
        formData.append('id', id);
        formData.append('reason', comment);
        formData.append('date_of', date_of);
        formData.append('contract', contract);

        return this._http.put(`/v1/employee/discharge-employee`, formData).pipe(
            take(1),
            map((response) => {
                if (+response.code === HttpStatusCode.Ok) {
                    return true;
                } else {
                    this._messageService.error(response.message, {
                        nzDuration: 5000,
                    });

                    return false;
                }
            }),
            catchError((error) => {
                this._fuseConfirmationService.open({
                    title: 'Oops!',
                    message: error.message,
                    actions: {
                        confirm: {
                            show: false,
                        },
                        cancel: {
                            label: 'OK',
                        },
                    },
                });

                throw new Error(
                    'An error occurred while discharge the part-timer, please try again later.'
                );
            })
        );
    }

    public handleUploadAgeDoc(contract: File, id: string) {
        const formData = new FormData();

        // Append all text fields
        formData.append('id', id);
        formData.append('doc', contract);

        return this._http.put(`/v1/employee/update-age-doc`, formData).pipe(
            take(1),
            map((response) => {
                if (+response.code === HttpStatusCode.Ok) {
                    return true;
                } else {
                    this._messageService.error(response.message, {
                        nzDuration: 5000,
                    });

                    return false;
                }
            }),
            catchError((error) => {
                this._fuseConfirmationService.open({
                    title: 'Oops!',
                    message: error.message,
                    actions: {
                        confirm: {
                            show: false,
                        },
                        cancel: {
                            label: 'OK',
                        },
                    },
                });

                throw new Error(
                    'An error occurred while updating the part-timer age document, please try again later.'
                );
            })
        );
    }

    public getPartTimerWorkingListWithPagination(
        employeeId: string,
        page_number: number = 0,
        page_size: number = 25,
        search: string = null,
        sort: string = 'id',
        order: 'asc' | 'desc' | '' = 'desc'
    ) {
        return this._http
            .post<PartTimerWorkingHoursPaginationInterface>(
                `/v1/roster/finance/employee-schedule/${employeeId}`,
                null,
                {
                    page_size: page_size,
                    page_num: page_number + 1,
                    sort_by: sort,
                    sort_direction: order,
                    keyword: search || '',
                }
            )
            .pipe(
                map((response) => {
                    if (+response.code === HttpStatusCode.Ok) {
                        const records = response.data.records;
                        console.log('Records: ', records);

                        this._paginationHistory.next(response.data);
                        this._partTimerWorkingHistoryList.next(records);
                    } else {
                        this._messageService.error(response.message, {
                            nzDuration: 5000,
                        });
                    }
                }),
                catchError((error) => {
                    this._fuseConfirmationService.open({
                        title: 'Oops!',
                        message: error.message,
                        actions: {
                            confirm: {
                                show: false,
                            },
                            cancel: {
                                label: 'OK',
                            },
                        },
                    });

                    throw new Error(
                        'An error occurred while fetching the part-timer list, please try again later.'
                    );
                })
            );
    }

    public appealPTById(id: string, comment: string) {
        return this._http
            .put(`/v1/employee/appeal-discharge-pt`, null, {
                id,
                comment,
            })
            .pipe(
                take(1),
                map((response) => {
                    if (+response.code === HttpStatusCode.Ok) {
                        return true;
                    } else {
                        this._messageService.error(response.message, {
                            nzDuration: 5000,
                        });

                        return false;
                    }
                }),
                catchError((error) => {
                    this._fuseConfirmationService.open({
                        title: 'Oops!',
                        message: error.message,
                        actions: {
                            confirm: {
                                show: false,
                            },
                            cancel: {
                                label: 'OK',
                            },
                        },
                    });

                    throw new Error(
                        'An error occurred while appeal the part-timer, please try again later.'
                    );
                })
            );
    }

    public rejoinPTById(id: string, comment: string) {
        return this._http
            .put(`/v1/employee/rejoin-discharge-pt`, null, {
                id,
                comment,
            })
            .pipe(
                take(1),
                map((response) => {
                    if (+response.code === HttpStatusCode.Ok) {
                        // Remove the employee from the employee list
                        const updatedEmployeeList =
                            this._partTimerCurrentDischargeList
                                .getValue()
                                .filter((employee) => employee.id !== id);
                        this._partTimerCurrentDischargeList.next(
                            updatedEmployeeList
                        );

                        // Update the total count
                        this._paginationCurrentDischarge.next({
                            ...this._paginationCurrentDischarge.getValue(),
                            total_records:
                                +this._paginationCurrentDischarge.getValue()
                                    ?.total_records - 1,
                        });

                        return true;
                    } else {
                        this._messageService.error(response.message, {
                            nzDuration: 5000,
                        });

                        return false;
                    }
                }),
                catchError((error) => {
                    this._fuseConfirmationService.open({
                        title: 'Oops!',
                        message: error.message,
                        actions: {
                            confirm: {
                                show: false,
                            },
                            cancel: {
                                label: 'OK',
                            },
                        },
                    });

                    throw new Error(
                        'An error occurred while rejected the part-timer, please try again later.'
                    );
                })
            );
    }

    public rejectedDischargePTById(id: string, comment: string) {
        return this._http
            .put(`/v1/employee/rejected-discharge-pt`, null, {
                id,
                comment,
            })
            .pipe(
                take(1),
                map((response) => {
                    if (+response.code === HttpStatusCode.Ok) {
                        // Remove the employee from the employee list
                        const updatedEmployeeList = this._partTimerDischargeList
                            .getValue()
                            .filter((employee) => employee.id !== id);
                        this._partTimerDischargeList.next(updatedEmployeeList);

                        // Update the total count
                        this._pagination.next({
                            ...this._pagination.getValue(),
                            total_records:
                                +this._pagination.getValue()?.total_records - 1,
                        });

                        return true;
                    } else {
                        this._messageService.error(response.message, {
                            nzDuration: 5000,
                        });

                        return false;
                    }
                }),
                catchError((error) => {
                    this._fuseConfirmationService.open({
                        title: 'Oops!',
                        message: error.message,
                        actions: {
                            confirm: {
                                show: false,
                            },
                            cancel: {
                                label: 'OK',
                            },
                        },
                    });

                    throw new Error(
                        'An error occurred while rejected the part-timer, please try again later.'
                    );
                })
            );
    }

    public assignEmployee(
        employeeId: string,
        managerId: string,
        notes: string
    ) {
        return this._http
            .post(`/v1/employee/assign`, null, {
                employee_id: employeeId,
                manager_id: managerId,
                notes: notes,
            })
            .pipe(
                take(1),
                map((response) => {
                    if (+response.code === HttpStatusCode.Ok) {
                        this._messageService.success(
                            'Manager access granted successfully',
                            {
                                nzDuration: 3000,
                            }
                        );

                        return true;
                    } else {
                        this._messageService.error(response.message, {
                            nzDuration: 5000,
                        });

                        return false;
                    }
                }),
                catchError((error) => {
                    this._fuseConfirmationService.open({
                        title: 'Oops!',
                        message: error.message,
                        actions: {
                            confirm: {
                                show: false,
                            },
                            cancel: {
                                label: 'OK',
                            },
                        },
                    });

                    throw new Error(
                        'An error occurred while granting manager access, please try again later.'
                    );
                })
            );
    }

    public revokeEmployeeAssignment(assignmentId: number) {
        return this._http
            .delete(`/v1/employee/revoke-assignment`, {
                assignment_id: assignmentId,
            })
            .pipe(
                take(1),
                map((response) => {
                    if (+response.code === HttpStatusCode.Ok) {
                        this._messageService.success(
                            'Manager access revoked successfully',
                            {
                                nzDuration: 3000,
                            }
                        );

                        return true;
                    } else {
                        this._messageService.error(response.message, {
                            nzDuration: 5000,
                        });

                        return false;
                    }
                }),
                catchError((error) => {
                    this._fuseConfirmationService.open({
                        title: 'Oops!',
                        message: error.message,
                        actions: {
                            confirm: {
                                show: false,
                            },
                            cancel: {
                                label: 'OK',
                            },
                        },
                    });

                    throw new Error(
                        'An error occurred while revoking manager access, please try again later.'
                    );
                })
            );
    }

    public approvedDischargePTById(id: string, comment: string) {
        return this._http
            .put(`/v1/employee/approve-discharge-pt`, null, {
                id,
                comment,
            })
            .pipe(
                take(1),
                map((response) => {
                    if (+response.code === HttpStatusCode.Ok) {
                        // Remove the employee from the employee list
                        const updatedEmployeeList = this._partTimerDischargeList
                            .getValue()
                            .filter((employee) => employee.id !== id);
                        this._partTimerDischargeList.next(updatedEmployeeList);

                        // Update the total count
                        this._pagination.next({
                            ...this._pagination.getValue(),
                            total_records:
                                +this._pagination.getValue()?.total_records - 1,
                        });

                        return true;
                    } else {
                        this._messageService.error(response.message, {
                            nzDuration: 5000,
                        });

                        return false;
                    }
                }),
                catchError((error) => {
                    this._fuseConfirmationService.open({
                        title: 'Oops!',
                        message: error.message,
                        actions: {
                            confirm: {
                                show: false,
                            },
                            cancel: {
                                label: 'OK',
                            },
                        },
                    });

                    throw new Error(
                        'An error occurred while approved the part-timer, please try again later.'
                    );
                })
            );
    }

    public getMonthlyShift(employeeId: string) {
        return this._http
            .get<any>(`/v1/employee/get-monthly-shift`, {
                id: employeeId,
            })
            .pipe(
                take(1),
                map((response) => {
                    if (+response.code === HttpStatusCode.Ok) {
                        return response.data;
                    } else {
                        this._messageService.error(response.message, {
                            nzDuration: 5000,
                        });
                        return null;
                    }
                }),
                catchError((error) => {
                    this._fuseConfirmationService.open({
                        title: 'Oops!',
                        message: error.message,
                        actions: {
                            confirm: {
                                show: false,
                            },
                            cancel: {
                                label: 'OK',
                            },
                        },
                    });

                    throw new Error(
                        'An error occurred while fetching monthly shifts, please try again later.'
                    );
                })
            );
    }
}
